import type { PasskeyApi, PasskeyAssertion, PasskeyRegistration, SessionState } from "../api/client";

import { device } from "../api/client";

/**
 * Die **biometrische Anmeldung** im Browser (Skill `silvererp-auth`,
 * `references/biometrie.md`; #159).
 *
 * Das Schlüsselpaar entsteht im gesicherten Bereich des Geräts, der private
 * Schlüssel verlässt ihn nie, und das SilverERP kennt nur den öffentlichen. Es
 * wird also kein Passwort abgelegt — das ist der ganze Unterschied zwischen
 * Biometrie und „Anmeldedaten merken".
 *
 * Beide Zeremonien haben dieselbe Gestalt und laufen über je zwei Aufrufe:
 * **Probe holen → Gerätedialog → Ergebnis abliefern.** Zweimal derselbe Ablauf
 * mit zwei Fehlerbehandlungen wäre zweimal dieselbe Gelegenheit, ihn falsch zu
 * machen; deshalb steht der Gang zum Gerät hier einmal da.
 *
 * **Zwei Dinge tut dieser Code ausdrücklich nicht.** Er *deutet die Probe
 * nicht*: Sie kommt vom SilverERP, ihre Felder gehören ihm, und was hier
 * geschieht, ist allein die Umrechnung der Bytefelder in Puffer, weil die
 * Schnittstelle des Browsers dort Puffer verlangt. Und er *prüft nichts*: Die
 * Unterschrift prüft das SilverERP, ein Nachbau wäre eine zweite, ungeprüfte
 * Prüfung.
 *
 * **Die Kodierung liegt nicht hier.** Der Browser liefert immer Base64URL; dass
 * der Abschluss der Anmeldung daraus Standard-Base64 macht und der des
 * Einrichtens nicht, ist eine Eigenheit des SilverERP und steht an der Naht
 * dorthin (`src/Erp/StandardBase64.php`).
 */

/**
 * Der Mensch hat den Gerätedialog weggetippt. Ein normaler Vorgang und kein
 * Fehler — er wird deshalb nicht rot gezeigt und gar nicht gezeigt: Wer den
 * Dialog wegtippt, hat sich entschieden.
 */
export class Aborted extends Error {
  public constructor() {
    super("Die biometrische Anmeldung wurde abgebrochen.");
    this.name = "Aborted";
  }
}

/**
 * Ob dieser Browser die Zeremonie überhaupt anbieten kann.
 *
 * Ohne gesicherten Kontext stellt er die Schnittstelle gar nicht bereit — im
 * Firmennetz ohne TLS ist das der Normalfall und kein Fehler. Dann gibt es die
 * Biometrie eben nicht, und der Weg über das Passwort steht weiter offen.
 */
export async function biometricsAvailable(): Promise<boolean> {
  if (typeof globalThis.PublicKeyCredential !== "function") {
    return false;
  }

  try {
    return await globalThis.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Ob auf **diesem** Gerät ein biometrischer Zugang eingerichtet ist.
 *
 * Die Frage lässt sich nicht an der Gegenstelle stellen: Sie kennt keinen
 * Endpunkt, der eingerichtete Zugänge aufzählt, und vor der Anmeldung gäbe es
 * ohnehin keine Sitzung, unter der man fragen könnte. Was hier steht, ist
 * deshalb eine Marke dieses Browsers — gesetzt, wenn das Einrichten
 * durchgelaufen ist.
 *
 * Sie ist **kein Geheimnis und kein Nachweis**: Wer sie von Hand setzt, bekommt
 * einen Knopf, hinter dem die Gegenstelle „nein" sagt. Ihre einzige Aufgabe ist
 * die Antwort auf die Frage, ob der Knopf im Anmeldebild stehen darf.
 */
export function passkeyEnrolled(store: Storage = localStorage): boolean {
  return store.getItem(PASSKEY_KEY) === device(store);
}

/** Merkt sich, dass auf diesem Gerät ein Zugang eingerichtet wurde. */
export function rememberPasskey(store: Storage = localStorage): void {
  store.setItem(PASSKEY_KEY, device(store));
}

/**
 * Vergisst den Zugang auf diesem Gerät.
 *
 * **Das ist kein Widerruf an der Gegenstelle**: Einen Endpunkt dafür gibt es
 * nicht, das SilverERP hält den Zugangsschlüssel und ist für ihn die Wahrheit.
 * Was hier geschieht, ist genau das, was ein Produkt über *dieses* Gerät
 * entscheiden kann — der Knopf im Anmeldebild fällt weg.
 */
export function forgetPasskey(store: Storage = localStorage): void {
  store.removeItem(PASSKEY_KEY);
}

/**
 * Ob das Anmeldebild den Knopf zeigt: eingerichtet **und** von diesem Browser
 * überhaupt zu bedienen.
 *
 * Die Antwort steht **vor dem ersten Zeichnen** fest (`FRAME.md`, „Die
 * biometrische Anmeldung"). Ein Knopf, der nachgereicht wird, sobald eine
 * Prüfung zurückkommt, springt in eine Karte hinein, die schon steht, und
 * verschiebt alles darunter. Die Prüfung ist asynchron; die Karte wartet auf sie.
 */
export async function passkeyOffered(store: Storage = localStorage): Promise<boolean> {
  return passkeyEnrolled(store) && (await biometricsAvailable());
}

/**
 * Die ganze Anmeldezeremonie: Probe holen, Gerätedialog, Ergebnis abliefern.
 *
 * @throws Aborted wenn der Mensch den Gerätedialog weggetippt hat
 */
export async function signInWithPasskey(api: PasskeyApi): Promise<SessionState> {
  const id = device();
  const challenge = await api.passkeyChallenge(id);
  const assertion = await signInCeremony(challenge, id);

  return api.signInWithPasskey(assertion);
}

/**
 * Die ganze Einrichtungszeremonie. Sie braucht eine laufende Sitzung — die
 * beiden Aufrufe liegen hinter der Sitzungsabsicherung.
 *
 * Danach steht die Marke dieses Browsers, und erst danach: Was nicht
 * durchgelaufen ist, wird nicht gemerkt.
 *
 * @throws Aborted wenn der Mensch den Gerätedialog weggetippt hat
 */
export async function setUpPasskey(api: PasskeyApi): Promise<void> {
  if (!(await biometricsAvailable())) {
    throw new Error(
      "Dieser Browser bietet keine biometrische Anmeldung an. Ohne verschlüsselte Verbindung " +
        "stellt er die Schnittstelle nicht bereit.",
    );
  }

  const id = device();
  const challenge = await api.passkeySetupChallenge(id);
  const registration = await registrationCeremony(challenge, id);

  await api.setUpPasskey(registration);
  rememberPasskey();
}

/** Der Gang zum Gerät bei der Anmeldung. */
export async function signInCeremony(challenge: unknown, id: string): Promise<PasskeyAssertion> {
  const assertion = await ceremony(
    "get",
    withBuffers(challenge, ["challenge"], ["allowCredentials"]),
  );
  const answer = assertion.response as AuthenticatorAssertionResponse;

  return {
    credentialId: assertion.id,
    authenticatorData: toBase64url(answer.authenticatorData),
    clientData: toBase64url(answer.clientDataJSON),
    signature: toBase64url(answer.signature),
    userHandle: answer.userHandle === null ? null : toBase64url(answer.userHandle),
    device: id,
  };
}

/** Der Gang zum Gerät beim Einrichten. */
export async function registrationCeremony(
  challenge: unknown,
  id: string,
): Promise<PasskeyRegistration> {
  const assertion = await ceremony(
    "create",
    withBuffers(challenge, ["challenge"], ["excludeCredentials"], "user"),
  );
  const answer = assertion.response as AuthenticatorAttestationResponse;

  return {
    credentialId: assertion.id,
    attestation: toBase64url(answer.attestationObject),
    clientData: toBase64url(answer.clientDataJSON),
    device: id,
    // Eine Bezeichnung des Geräts fragt dieses Werkzeug nicht ab. Sie wäre
    // brauchbar in einer Liste eingerichteter Geräte, und die gibt es hier
    // nicht: Dieser Dialog kennt genau dieses Gerät.
    label: null,
  };
}

/**
 * Base64URL → Puffer. Gebraucht, weil die Probe als Zeichenkette kommt und die
 * Schnittstelle des Browsers Puffer verlangt.
 *
 * Kein Fremdpaket dafür — es sind vierzig Zeilen, und sie sind für sich prüfbar.
 * `PublicKeyCredential.parseRequestOptionsFromJSON` täte dasselbe, hängt aber an
 * einer Browserversion, und ein Anmeldeweg, der auf einem älteren Browser still
 * nicht mehr geht, ist genau der Ausfall, den niemand bemerkt.
 */
export function fromBase64url(text: string): ArrayBuffer {
  const standard = text.replace(/-/g, "+").replace(/_/g, "/");
  const rest = standard.length % 4;
  const padded = rest === 0 ? standard : standard + "=".repeat(4 - rest);
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index++) {
    bytes[index] = raw.charCodeAt(index);
  }

  return bytes.buffer;
}

/** Puffer → Base64URL. Die Gegenrichtung: Die Zeremonie gibt Puffer her. */
export function toBase64url(data: ArrayBuffer): string {
  const bytes = new Uint8Array(data);
  let raw = "";

  /* Zeichenweise und nicht über `String.fromCharCode(...bytes)`: Ein
     Attestationsobjekt sind einige Kilobyte, und ein Aufruf mit einigen tausend
     Argumenten überschreitet die Argumentgrenze der Laufzeit. */
  for (const byte of bytes) {
    raw += String.fromCharCode(byte);
  }

  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Dieselbe Probe, nur mit Bytepuffern an den Stellen, an denen die Schnittstelle
 * des Browsers Puffer verlangt.
 *
 * **Nach Feldnamen und nicht nach einem Schema** — die Probe gehört der
 * Gegenstelle. Was nicht genannt ist, geht unverändert durch; ein Typ darüber
 * wäre eine zweite Beschreibung von etwas, das wir nicht besitzen, und sie würde
 * bei jedem Feld falsch, das die Gegenstelle ergänzt.
 */
export function withBuffers(
  challenge: unknown,
  buffers: readonly string[],
  lists: readonly string[],
  nested?: string,
): Record<string, unknown> {
  const fields = { ...(challenge as Record<string, unknown>) };

  for (const name of buffers) {
    const value = fields[name];

    if (typeof value === "string") {
      fields[name] = fromBase64url(value);
    }
  }

  for (const name of lists) {
    const value = fields[name];

    if (Array.isArray(value)) {
      fields[name] = (value as Record<string, unknown>[]).map((entry) => ({
        ...entry,
        id: typeof entry["id"] === "string" ? fromBase64url(entry["id"]) : entry["id"],
      }));
    }
  }

  if (nested !== undefined) {
    const value = fields[nested];

    if (typeof value === "object" && value !== null) {
      const inner = value as Record<string, unknown>;
      fields[nested] = {
        ...inner,
        id: typeof inner["id"] === "string" ? fromBase64url(inner["id"]) : inner["id"],
      };
    }
  }

  return fields;
}

/**
 * Der Gang zum Gerät, für beide Zeremonien.
 *
 * `NotAllowedError` heißt „abgebrochen oder abgelaufen" — der Dialog
 * unterscheidet das nicht, und die Probe gilt ohnehin nur eine Minute. Beides
 * ist hier ein Abbruch und keine Absage.
 */
async function ceremony(
  kind: "get" | "create",
  options: Record<string, unknown>,
): Promise<PublicKeyCredential> {
  const init = { publicKey: options } as unknown as CredentialRequestOptions &
    CredentialCreationOptions;
  let assertion: Credential | null;

  try {
    assertion =
      kind === "get"
        ? await navigator.credentials.get(init)
        : await navigator.credentials.create(init);
  } catch (reason: unknown) {
    if (reason instanceof DOMException && reason.name === "NotAllowedError") {
      throw new Aborted();
    }

    throw reason;
  }

  if (assertion === null) {
    throw new Aborted();
  }

  return assertion as PublicKeyCredential;
}

/**
 * Die Marke dieses Browsers. Sie trägt die **Gerätekennung** als Wert und nicht
 * bloß eine Eins: Bekommt der Browser eine neue Gerätekennung — geleerte Ablage,
 * anderes Profil —, dann gilt der alte Zugang für die Gegenstelle ohnehin nicht
 * mehr, und die Marke fällt mit ihm.
 */
const PASSKEY_KEY = "devtools.passkey";
